import { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Download, FileText, Lock, Calendar, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

interface TherapistExportProps {
  relationshipId: string;
  tier: 'free' | 'premium' | 'pro';
}

export function TherapistExport({ relationshipId, tier }: TherapistExportProps) {
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'all'>('month');
  const [includeJournal, setIncludeJournal] = useState(true);
  const [includeAnxiety, setIncludeAnxiety] = useState(true);
  const [includeAttachment, setIncludeAttachment] = useState(true);
  const [anonymize, setAnonymize] = useState(false);

  const isPremium = tier !== 'free';
  const isPro = tier === 'pro';

  const generateReport = () => {
    // Get data
    const allCheckins = JSON.parse(localStorage.getItem('tension_checkins') || '[]');
    const allJournal = JSON.parse(localStorage.getItem('tension_journal_entries') || '[]');
    const allAnxiety = JSON.parse(localStorage.getItem('tension_anxiety_tracker') || '[]');
    const allAttachment = JSON.parse(localStorage.getItem('tension_attachment_assessments') || '[]');
    const relationships = JSON.parse(localStorage.getItem('tension_relationships') || '[]');

    // Filter by relationship
    const checkins = allCheckins.filter((c: any) => c.relationshipId === relationshipId);
    const journal = allJournal.filter((j: any) => j.relationshipId === relationshipId);
    const anxiety = allAnxiety.filter((a: any) => a.relationshipId === relationshipId);
    const attachment = allAttachment.filter((a: any) => a.relationshipId === relationshipId);
    const relationship = relationships.find((r: any) => r.id === relationshipId);

    // Filter by date range
    const now = new Date();
    let startDate: Date;
    
    switch (dateRange) {
      case 'week':
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 7);
        break;
      case 'month':
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
        break;
      case 'all':
      default:
        startDate = new Date(0); // Beginning of time
    }

    const filteredCheckins = checkins.filter((c: any) => new Date(c.timestamp) >= startDate);
    const filteredJournal = journal.filter((j: any) => new Date(j.timestamp) >= startDate);
    const filteredAnxiety = anxiety.filter((a: any) => new Date(a.timestamp) >= startDate);

    // Calculate stats
    const avgRTI = filteredCheckins.length > 0
      ? Math.round(filteredCheckins.reduce((sum: number, c: any) => sum + (c.rtiScore || 0), 0) / filteredCheckins.length)
      : 0;

    const avgComponents = filteredCheckins.length > 0
      ? {
          initiation: Math.round(filteredCheckins.reduce((sum: number, c: any) => sum + (c.components?.initiation || 0), 0) / filteredCheckins.length),
          latency: Math.round(filteredCheckins.reduce((sum: number, c: any) => sum + (c.components?.latency || 0), 0) / filteredCheckins.length),
          emotionalDensity: Math.round(filteredCheckins.reduce((sum: number, c: any) => sum + (c.components?.emotionalDensity || 0), 0) / filteredCheckins.length),
          availability: Math.round(filteredCheckins.reduce((sum: number, c: any) => sum + (c.components?.availability || 0), 0) / filteredCheckins.length),
          withdrawal: Math.round(filteredCheckins.reduce((sum: number, c: any) => sum + (c.components?.withdrawal || 0), 0) / filteredCheckins.length),
        }
      : null;

    const highTensionDays = filteredCheckins.filter((c: any) => (c.rtiScore || 0) > 60).length;
    const stableDays = filteredCheckins.filter((c: any) => (c.rtiScore || 0) <= 35).length;

    // Anonymization
    const partnerName = anonymize ? '[Partner]' : relationship?.name || '[Partner]';

    // Generate text report
    const report = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
TENSION APP - THERAPIST SUMMARY REPORT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
Report Period: ${dateRange === 'week' ? 'Last 7 days' : dateRange === 'month' ? 'Last 30 days' : 'All time'}
Relationship: ${partnerName}
Type: ${relationship?.type || 'Unknown'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
OVERVIEW
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Total Check-Ins: ${filteredCheckins.length}
Average RTI (Relational Tension Index): ${avgRTI}/100
High Tension Days (RTI > 60): ${highTensionDays} (${filteredCheckins.length > 0 ? Math.round((highTensionDays / filteredCheckins.length) * 100) : 0}%)
Stable Days (RTI ≤ 35): ${stableDays} (${filteredCheckins.length > 0 ? Math.round((stableDays / filteredCheckins.length) * 100) : 0}%)

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
RTI COMPONENTS (Average Scores)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${avgComponents ? `
1. Initiation Imbalance: ${avgComponents.initiation}/100
   (Who initiates contact more often)
   ${avgComponents.initiation > 70 ? '⚠️ Client significantly over-pursuing' : avgComponents.initiation < 40 ? '⚠️ Partner significantly over-pursuing' : '✓ Balanced'}

2. Response Latency: ${avgComponents.latency}/100
   (Delays in communication)
   ${avgComponents.latency > 70 ? '⚠️ Significant delays creating anxiety' : '✓ Acceptable'}

3. Emotional Density: ${avgComponents.emotionalDensity}/100
   (Weight/seriousness of conversations)
   ${avgComponents.emotionalDensity > 70 ? '⚠️ Heavy, serious communication pattern' : '✓ Healthy balance'}

4. Over-Availability: ${avgComponents.availability}/100
   (Immediate responsiveness creating pressure)
   ${avgComponents.availability > 70 ? '⚠️ Client may be too available, losing mystery' : '✓ Healthy boundaries'}

5. Withdrawal Signals: ${avgComponents.withdrawal}/100
   (Partner pulling away)
   ${avgComponents.withdrawal > 70 ? '⚠️ Significant withdrawal detected' : '✓ Partner engaged'}
` : 'No component data available'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
CLINICAL OBSERVATIONS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${avgRTI > 60 ? 'Client is experiencing HIGH relationship tension. Recommend exploring attachment patterns and anxiety management.' : avgRTI > 35 ? 'Client experiencing MODERATE tension. Monitor for patterns and potential triggers.' : 'Client reports HEALTHY relationship state. Focus on maintenance and skill-building.'}

${avgComponents && avgComponents.initiation > 70 ? '\n⚠️ ANXIOUS PURSUIT PATTERN: Client is over-initiating contact. Explore fear of abandonment and need for external validation.\n' : ''}
${avgComponents && avgComponents.withdrawal > 70 ? '\n⚠️ PARTNER WITHDRAWAL: Possible avoidant attachment or relationship dissatisfaction. Explore couple\'s communication.\n' : ''}
${highTensionDays > filteredCheckins.length * 0.5 ? '\n⚠️ CHRONIC HIGH TENSION: Over 50% of days in high tension state. Consider deeper therapeutic intervention.\n' : ''}

${includeJournal && filteredJournal.length > 0 ? `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
JOURNAL ENTRIES (Context Notes)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Total Entries: ${filteredJournal.length}

${filteredJournal.slice(0, 10).map((entry: any, idx: number) => `
${idx + 1}. [${new Date(entry.timestamp).toLocaleDateString()}]
   "${entry.note}"
`).join('')}

${filteredJournal.length > 10 ? `\n(Showing 10 of ${filteredJournal.length} entries. Full export available in Pro version.)\n` : ''}
` : ''}

${includeAnxiety && filteredAnxiety.length > 0 ? `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
ANXIETY TRACKING
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Total Anxiety Logs: ${filteredAnxiety.length}
Predictions Resolved: ${filteredAnxiety.filter((a: any) => a.accuracy !== 'pending').length}
Accurate Predictions: ${filteredAnxiety.filter((a: any) => a.accuracy === 'accurate').length}
Inaccurate (Anxiety Lied): ${filteredAnxiety.filter((a: any) => a.accuracy === 'inaccurate').length}

${filteredAnxiety.filter((a: any) => a.accuracy === 'inaccurate').length > 0 ? `
⚠️ COGNITIVE DISTORTION: Client's anxious thoughts were inaccurate ${Math.round((filteredAnxiety.filter((a: any) => a.accuracy === 'inaccurate').length / filteredAnxiety.filter((a: any) => a.accuracy !== 'pending').length) * 100)}% of the time.
Recommend CBT techniques for challenging catastrophic thinking.
` : ''}

Recent Anxious Thoughts:
${filteredAnxiety.slice(0, 5).map((entry: any, idx: number) => `
${idx + 1}. [${new Date(entry.timestamp).toLocaleDateString()}] Severity: ${entry.severity}/10
   Thought: "${entry.anxiousThought}"
   Prediction: "${entry.prediction}"
   ${entry.actualOutcome ? `Reality: "${entry.actualOutcome}" (${entry.accuracy === 'accurate' ? 'Accurate' : 'Anxiety was wrong'})` : 'Pending'}
`).join('')}
` : ''}

${includeAttachment && attachment.length > 0 ? `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
ATTACHMENT STYLE ASSESSMENTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Total Assessments: ${attachment.length}
Current Style: ${attachment[0]?.style.toUpperCase().replace('-', ' ')}
First Assessment: ${attachment[attachment.length - 1]?.style.toUpperCase().replace('-', ' ')}

${attachment.length > 1 ? `
ATTACHMENT JOURNEY:
${attachment.slice().reverse().map((a: any, idx: number) => `
${idx + 1}. [${new Date(a.timestamp).toLocaleDateString()}]
   Style: ${a.style.replace('-', ' ').toUpperCase()}
   Anxious Score: ${a.anxiousScore}/35
   Avoidant Score: ${a.avoidantScore}/35
`).join('')}

${attachment[0]?.style !== attachment[attachment.length - 1]?.style ? '✓ Attachment style has evolved over time. Client shows progress in self-awareness.' : 'Attachment style consistent across assessments.'}
` : ''}
` : ''}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
RECOMMENDATIONS FOR THERAPY
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${avgRTI > 60 ? '1. Address anxious attachment patterns and fear of abandonment\n2. Teach self-soothing and emotional regulation techniques\n3. Explore childhood attachment experiences\n4. Practice cognitive reframing of catastrophic thoughts' : avgRTI > 35 ? '1. Monitor for escalation patterns\n2. Strengthen communication skills\n3. Build self-confidence and independence\n4. Address any trust issues' : '1. Reinforce healthy relationship patterns\n2. Maintain self-awareness practices\n3. Continue building secure attachment behaviors'}

${avgComponents && avgComponents.initiation > 70 ? '\n5. Work on reducing pursuit behaviors and building comfort with space' : ''}
${filteredAnxiety.filter((a: any) => a.accuracy === 'inaccurate').length >= 3 ? '\n6. CBT for challenging anxiety-driven predictions (evidence shows high inaccuracy rate)' : ''}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
DISCLAIMER
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

This report is generated from self-reported data via the TENSION app.
It is intended as a supplement to clinical assessment, not a replacement.
All interpretations should be validated through direct therapeutic conversation.

End of Report
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    `.trim();

    return report;
  };

  const handleDownloadText = () => {
    const report = generateReport();
    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tension-therapist-report-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    toast.success('Report downloaded');
  };

  const handleDownloadPDF = () => {
    // In production, this would generate actual PDF
    toast.success('PDF generation would happen here (requires Pro tier)');
    // Would use jsPDF or similar library
  };

  if (!isPremium) {
    return (
      <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Lock className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-neutral-900 mb-1">Therapist Export</h3>
            <p className="text-sm text-neutral-600 mb-3">
              Generate comprehensive reports of your TENSION data to share with your therapist or counselor.
            </p>
            <div className="bg-white/60 rounded-lg p-3 mb-3">
              <p className="text-xs text-neutral-700 mb-2">
                <strong>Includes:</strong>
              </p>
              <div className="space-y-1 text-xs text-neutral-600">
                <p>• RTI trends and component breakdowns</p>
                <p>• Journal entries with timestamps</p>
                <p>• Anxiety vs. Reality tracking</p>
                <p>• Attachment style assessments</p>
                <p>• Clinical observations and recommendations</p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => window.location.href = '/settings'}
              className="gap-2"
            >
              Unlock with Premium
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-600" />
          <h3 className="font-medium text-neutral-900">Therapist Export</h3>
          <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">
            {isPro ? 'PRO' : 'PREMIUM'}
          </span>
        </div>

        <p className="text-sm text-neutral-600">
          Generate a comprehensive report to share with your therapist or counselor.
        </p>

        {/* Options */}
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-neutral-900 mb-2 block">
              Date Range
            </label>
            <div className="grid grid-cols-3 gap-2">
              {(['week', 'month', 'all'] as const).map((range) => (
                <button
                  key={range}
                  onClick={() => setDateRange(range)}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                    dateRange === range
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-neutral-200 text-neutral-700 hover:border-blue-300'
                  }`}
                >
                  {range === 'week' && 'Last 7 Days'}
                  {range === 'month' && 'Last 30 Days'}
                  {range === 'all' && 'All Time'}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-neutral-900 block">
              Include in Report
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeJournal}
                  onChange={(e) => setIncludeJournal(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm text-neutral-700">Journal Entries</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeAnxiety}
                  onChange={(e) => setIncludeAnxiety(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm text-neutral-700">Anxiety Tracking (Pro)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeAttachment}
                  onChange={(e) => setIncludeAttachment(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm text-neutral-700">Attachment Assessments (Pro)</span>
              </label>
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={anonymize}
                onChange={(e) => setAnonymize(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-neutral-700">Anonymize partner name</span>
            </label>
          </div>
        </div>

        {/* Download Buttons */}
        <div className="grid grid-cols-2 gap-3 pt-4 border-t border-neutral-200">
          <Button
            onClick={handleDownloadText}
            variant="outline"
            className="gap-2"
          >
            <Download className="w-4 h-4" />
            Download .txt
          </Button>
          <Button
            onClick={handleDownloadPDF}
            disabled={!isPro}
            className="gap-2"
          >
            <FileText className="w-4 h-4" />
            {isPro ? 'Download PDF' : 'PDF (Pro Only)'}
          </Button>
        </div>

        <div className="bg-white/60 rounded-lg p-3 border border-blue-200">
          <p className="text-xs text-neutral-700">
            <strong>Privacy Note:</strong> Reports are generated locally on your device. 
            Nothing is sent to our servers. Share only with trusted mental health professionals.
          </p>
        </div>
      </div>
    </Card>
  );
}
